/* eslint-disable no-console */
/**
 * Centralized logging utility for Team Cost Splitter frontend
 *
 * Provides environment-aware logging that:
 * - Only shows debug/info logs in development
 * - Always shows warnings and errors
 * - Prevents sensitive data leakage in production
 *
 * Note: This file intentionally uses console.* methods as it's the logging abstraction layer
 */

const isDev = process.env.NODE_ENV === "development";

/**
 * Sanitize data before logging to prevent exposing sensitive information
 * @param {any} data - Data to sanitize
 * @returns {any} Sanitized data
 */
function sanitizeForLogging(data) {
  if (!data || typeof data !== "object") {
    return data;
  }

  const sensitiveKeys = [
    "password",
    "token",
    "access_token",
    "refresh_token",
    "secret",
    "api_key",
    "apiKey",
    "authorization",
    "cookie",
    "session",
  ];

  const sanitized = Array.isArray(data) ? [...data] : { ...data };

  for (const key in sanitized) {
    const lowerKey = key.toLowerCase();

    if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
      sanitized[key] = sanitizeForLogging(sanitized[key]);
    }
  }

  return sanitized;
}

export const logger = {
  /**
   * Debug logs - only shown in development
   * Use for detailed debugging information
   */
  debug: (...args) => {
    if (isDev) {
      console.log("[DEBUG]", ...args.map(sanitizeForLogging));
    }
  },

  /**
   * Info logs - only shown in development
   * Use for general informational messages
   */
  info: (...args) => {
    if (isDev) {
      console.info("[INFO]", ...args.map(sanitizeForLogging));
    }
  },

  /**
   * Warning logs - shown in all environments
   * Use for recoverable issues that need attention
   */
  warn: (...args) => {
    console.warn("[WARN]", ...args.map(sanitizeForLogging));
  },

  /**
   * Error logs - shown in all environments
   * Use for errors and exceptions
   */
  error: (...args) => {
    console.error("[ERROR]", ...args.map(sanitizeForLogging));
  },

  /**
   * Log API errors with context
   * @param {string} operation - Operation that failed
   * @param {Error} error - Error object
   */
  apiError: (operation, error) => {
    const errorData = {
      operation,
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      ...(isDev && { stack: error.stack, response: error.response?.data }),
    };
    console.error("[API ERROR]", sanitizeForLogging(errorData));
  },
};

export default logger;
